/**
 * ReleaseModal — "What's new" popup shown once per release on login.
 *
 * Triggers when:
 *   - User is authenticated
 *   - GET /releases returns releases the user hasn't seen
 *   - Dismissed by clicking "Got it" or the ✕ button
 *
 * On dismiss, updates user_preferences.last_seen_release_version
 * to the newest release version so the modal won't show again for
 * this release.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { prefsApi, type PlatformRelease } from '../../lib/api'
import { useAuthStore } from '../../lib/store'

const C = {
  bg:       '#141A0F',
  bgCard:   '#1A2212',
  border:   'rgba(255,255,255,0.1)',
  amber:    'var(--color-primary)',
  green:    '#2D6A2F',
  text:     '#F0EDE4',
  textMuted:'#A8A49C',
  textFaint:'#6B6760',
}

export function ReleaseModal() {
  const { token } = useAuthStore()
  const qc = useQueryClient()
  const [dismissed, setDismissed] = useState(false)

  const { data } = useQuery({
    queryKey: ['releases'],
    queryFn: () => prefsApi.getReleases().then(r => r.data),
    enabled: !!token,
    staleTime: 5 * 60_000, // don't re-fetch within the same session
  })

  const markSeen = useMutation({
    mutationFn: (version: string) => prefsApi.markReleaseSeen(version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases'] }),
  })

  if (!token || dismissed) return null
  if (!data?.releases?.length) return null

  const releases = data.releases
  const latest = releases[0]

  function handleDismiss() {
    markSeen.mutate(latest.version)
    setDismissed(true)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 16,
      fontFamily: '"DM Sans", system-ui, sans-serif',
    }}>
      <div style={{
        background: C.bg, border: `1px solid ${C.border}`,
        borderRadius: 20, width: '100%', maxWidth: 480,
        boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                color: C.amber, background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
                padding: '2px 8px', borderRadius: 99,
                textTransform: 'uppercase',
              }}>What's new</span>
              <span style={{ fontSize: 11, color: C.textFaint }}>v{latest.version}</span>
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: C.text, margin: 0 }}>
              {latest.title}
            </h2>
            {latest.published_at && (
              <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>
                {new Date(latest.published_at).toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric',
                })}
              </div>
            )}
          </div>
          <button
            onClick={handleDismiss}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: C.textFaint, fontSize: 18, padding: 4, lineHeight: 1,
            }}
            aria-label="Close"
          >✕</button>
        </div>

        {/* Bullets from latest release */}
        <div style={{ padding: '16px 24px' }}>
          {latest.summary_bullets.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {latest.summary_bullets.map((bullet, i) => (
                <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                    background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginTop: 1,
                  }}>
                    <span style={{ fontSize: 10, color: C.amber }}>✦</span>
                  </span>
                  <span style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{bullet}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Additional releases if multiple */}
          {releases.length > 1 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 6 }}>Also released since your last visit:</div>
              {releases.slice(1).map((r: PlatformRelease) => (
                <div key={r.id} style={{ fontSize: 12, color: C.textMuted, marginBottom: 3 }}>
                  <span style={{ color: C.textFaint }}>v{r.version}</span>{' '}
                  {r.title}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleDismiss}
            style={{
              background: C.green, color: '#fff', border: 'none',
              borderRadius: 10, padding: '9px 24px', fontSize: 13,
              cursor: 'pointer', fontWeight: 500,
            }}
          >Got it</button>
        </div>
      </div>
    </div>
  )
}
